import { z } from 'zod';

// ========== QUESTION TYPES ==========
export const questionTypeSchema = z.enum([
  'scale',
  'yes_no',
  'text',
  'single_choice',
  'multiple_choice',
]);
export type QuestionType = z.infer<typeof questionTypeSchema>;

// ========== SCALE CONFIGURATION ==========
export const scaleConfigSchema = z.object({
  min: z.number().default(1),
  max: z.number().default(5),
  minLabel: z.string().optional(),
  maxLabel: z.string().optional(),
});
export type ScaleConfig = z.infer<typeof scaleConfigSchema>;

// ========== CHOICE OPTION ==========
export const choiceOptionSchema = z.object({
  id: z.string(),
  text: z.string(),
  value: z.number(),
});
export type ChoiceOption = z.infer<typeof choiceOptionSchema>;

// ========== FRAMEWORK MAPPING ==========
export const frameworkMappingSchema = z.object({
  domain: z.string().optional(),
  capability: z.string().optional(),
  weight: z.number().default(1),
});
export type FrameworkMapping = z.infer<typeof frameworkMappingSchema>;

// ========== FORM QUESTION ==========
export const formQuestionSchema = z.object({
  id: z.string(),
  text: z.string(),
  description: z.string().optional(),
  type: questionTypeSchema,
  required: z.boolean().default(true),
  order: z.number(),
  scaleConfig: scaleConfigSchema.optional(),
  choices: z.array(choiceOptionSchema).optional(),
  frameworkMapping: frameworkMappingSchema.optional(),
});
export type FormQuestion = z.infer<typeof formQuestionSchema>;

// ========== FORM SECTION ==========
export const formSectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  order: z.number(),
  questions: z.array(formQuestionSchema),
});
export type FormSection = z.infer<typeof formSectionSchema>;

// ========== FORM SETTINGS ==========
export const formSettingsSchema = z.object({
  showProgressBar: z.boolean().default(true),
  allowSaveProgress: z.boolean().default(true),
  randomizeQuestions: z.boolean().default(false),
  showSectionTitles: z.boolean().default(true),
});
export type FormSettings = z.infer<typeof formSettingsSchema>;

// ========== FORM STRUCTURE ==========
export const formStructureSchema = z.object({
  sections: z.array(formSectionSchema),
  settings: formSettingsSchema,
});
export type FormStructure = z.infer<typeof formStructureSchema>;

// ========== DIAGNOSTIC FRAMEWORK ==========
export const diagnosticFrameworkSchema = z.enum(['togaf10', 'cobit2019', 'custom']);
export type DiagnosticFramework = z.infer<typeof diagnosticFrameworkSchema>;

// ========== FORM STATUS ==========
export const formStatusSchema = z.enum(['draft', 'published', 'archived']);
export type FormStatus = z.infer<typeof formStatusSchema>;

// ========== RESPONSE STATUS ==========
export const responseStatusSchema = z.enum(['in_progress', 'completed']);
export type ResponseStatus = z.infer<typeof responseStatusSchema>;

// ========== DIAGNOSTIC STATUS ==========
export const diagnosticStatusSchema = z.enum(['draft', 'published']);
export type DiagnosticStatus = z.infer<typeof diagnosticStatusSchema>;

// ========== PORTAL ACTIVITY EVENT ==========
export const formPortalActivityEventSchema = z.enum([
  'access',
  'start',
  'complete',
  'view_diagnostic',
]);
export type FormPortalActivityEvent = z.infer<typeof formPortalActivityEventSchema>;

// ========== DIAGNOSTIC FORM ==========
export const diagnosticFormSchema = z.object({
  id: z.string(),
  userId: z.string(),
  clientId: z.string().nullable(),
  projectId: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  framework: diagnosticFrameworkSchema,
  structure: formStructureSchema,
  status: formStatusSchema,
  version: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DiagnosticForm = z.infer<typeof diagnosticFormSchema>;

// ========== FORM PORTAL ==========
export const formPortalSchema = z.object({
  id: z.string(),
  formId: z.string(),
  token: z.string(),
  hasPassword: z.boolean(),
  isActive: z.boolean(),
  expiresAt: z.string().nullable(),
  maxResponses: z.number().nullable(),
  brandingConfig: z.object({
    logo: z.string().optional(),
    primaryColor: z.string().optional(),
    companyName: z.string().optional(),
  }).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FormPortal = z.infer<typeof formPortalSchema>;

// ========== FORM RESPONSE ==========
export const formResponseSchema = z.object({
  id: z.string(),
  formId: z.string(),
  portalId: z.string().nullable(),
  respondentName: z.string().nullable(),
  respondentEmail: z.string().nullable(),
  respondentRole: z.string().nullable(),
  answers: z.record(z.string(), z.unknown()),
  status: responseStatusSchema,
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FormResponse = z.infer<typeof formResponseSchema>;

// ========== MATURITY SCORE ==========
export const maturityScoreSchema = z.object({
  overall: z.number(),
  bySection: z.record(z.string(), z.number()),
});
export type MaturityScore = z.infer<typeof maturityScoreSchema>;

// ========== FORM DIAGNOSTIC (AI Analysis) ==========
export const formDiagnosticSchema = z.object({
  id: z.string(),
  formId: z.string(),
  responseId: z.string().nullable(),
  title: z.string(),
  content: z.string(),
  maturityScore: maturityScoreSchema.nullable(),
  recommendations: z.array(z.string()).nullable(),
  status: diagnosticStatusSchema,
  generatedAt: z.string().nullable(),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FormDiagnostic = z.infer<typeof formDiagnosticSchema>;

// ========== REQUEST SCHEMAS ==========

// Create diagnostic form
export const createDiagnosticFormRequestSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  framework: diagnosticFrameworkSchema,
  structure: formStructureSchema,
  clientId: z.string().optional(),
});
export type CreateDiagnosticFormRequest = z.infer<typeof createDiagnosticFormRequestSchema>;

// Update diagnostic form
export const updateDiagnosticFormRequestSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(1000).optional(),
  structure: formStructureSchema.optional(),
  status: formStatusSchema.optional(),
});
export type UpdateDiagnosticFormRequest = z.infer<typeof updateDiagnosticFormRequestSchema>;

// Associate form to client
export const associateFormRequestSchema = z.object({
  clientId: z.string(),
});
export type AssociateFormRequest = z.infer<typeof associateFormRequestSchema>;

// Create/update portal
export const updateFormPortalRequestSchema = z.object({
  password: z.string().min(4).max(50).optional().nullable(),
  isActive: z.boolean().optional(),
  expiresAt: z.string().optional().nullable(),
  maxResponses: z.number().min(1).optional().nullable(),
  brandingConfig: z.object({
    logo: z.string().optional(),
    primaryColor: z.string().optional(),
    companyName: z.string().optional(),
  }).optional().nullable(),
});
export type UpdateFormPortalRequest = z.infer<typeof updateFormPortalRequestSchema>;

// Submit response
export const submitResponseRequestSchema = z.object({
  respondentName: z.string().max(100).optional(),
  respondentEmail: z.string().email().optional(),
  respondentRole: z.string().max(100).optional(),
  answers: z.record(z.string(), z.unknown()),
});
export type SubmitResponseRequest = z.infer<typeof submitResponseRequestSchema>;

// Start response (public)
export const startResponseRequestSchema = z.object({
  respondentName: z.string().max(100).optional(),
  respondentEmail: z.string().email().optional(),
  respondentRole: z.string().max(100).optional(),
});
export type StartResponseRequest = z.infer<typeof startResponseRequestSchema>;

// Update response (public)
export const updateResponseRequestSchema = z.object({
  answers: z.record(z.string(), z.unknown()),
});
export type UpdateResponseRequest = z.infer<typeof updateResponseRequestSchema>;

// Generate form with AI
export const generateFormRequestSchema = z.object({
  content: z.string().min(10).max(10000),
  framework: diagnosticFrameworkSchema.optional(),
  instructions: z.string().max(2000).optional(),
  locale: z.enum(['es', 'en']).optional(),
});
export type GenerateFormRequest = z.infer<typeof generateFormRequestSchema>;

// Generate analysis with AI
export const generateAnalysisRequestSchema = z.object({
  responseIds: z.array(z.string()).optional(),
  locale: z.enum(['es', 'en']).optional(),
});
export type GenerateAnalysisRequest = z.infer<typeof generateAnalysisRequestSchema>;

// ========== RESPONSE SCHEMAS ==========

// Diagnostic forms list
export const diagnosticFormsResponseSchema = z.object({
  forms: z.array(diagnosticFormSchema.extend({
    responseCount: z.number().optional(),
    portal: formPortalSchema.optional(),
  })),
});
export type DiagnosticFormsResponse = z.infer<typeof diagnosticFormsResponseSchema>;

// Single form response
export const diagnosticFormResponseSchema = z.object({
  form: diagnosticFormSchema.extend({
    responseCount: z.number().optional(),
    portal: formPortalSchema.optional(),
    client: z.object({
      id: z.string(),
      name: z.string(),
      company: z.string().nullable(),
    }).optional(),
  }),
});
export type DiagnosticFormResponse = z.infer<typeof diagnosticFormResponseSchema>;

// Form responses list
export const formResponsesListResponseSchema = z.object({
  responses: z.array(formResponseSchema),
});
export type FormResponsesListResponse = z.infer<typeof formResponsesListResponseSchema>;

// Form diagnostics list
export const formDiagnosticsListResponseSchema = z.object({
  diagnostics: z.array(formDiagnosticSchema),
});
export type FormDiagnosticsListResponse = z.infer<typeof formDiagnosticsListResponseSchema>;

// Templates response
export const diagnosticTemplatesResponseSchema = z.object({
  templates: z.array(z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    framework: diagnosticFrameworkSchema,
    questionCount: z.number(),
    sectionCount: z.number(),
  })),
});
export type DiagnosticTemplatesResponse = z.infer<typeof diagnosticTemplatesResponseSchema>;

// Public portal info
export const publicFormInfoResponseSchema = z.object({
  title: z.string(),
  description: z.string().nullable(),
  hasPassword: z.boolean(),
  isActive: z.boolean(),
  brandingConfig: z.object({
    logo: z.string().optional(),
    primaryColor: z.string().optional(),
    companyName: z.string().optional(),
  }).nullable(),
});
export type PublicFormInfoResponse = z.infer<typeof publicFormInfoResponseSchema>;

// Public form structure
export const publicFormStructureResponseSchema = z.object({
  formId: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  structure: formStructureSchema,
});
export type PublicFormStructureResponse = z.infer<typeof publicFormStructureResponseSchema>;

// AI generated form response
export const generatedFormResponseSchema = z.object({
  title: z.string(),
  description: z.string(),
  structure: formStructureSchema,
});
export type GeneratedFormResponse = z.infer<typeof generatedFormResponseSchema>;

// AI generated analysis response
export const generatedAnalysisResponseSchema = z.object({
  diagnostic: formDiagnosticSchema,
});
export type GeneratedAnalysisResponse = z.infer<typeof generatedAnalysisResponseSchema>;
